import { useState, useRef, useEffect } from "react"
import Slider from "react-slick"
import PropTypes from "prop-types"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import Styles from "./index.module.scss"

const visibleFull = 5
const peekRatio = 0.2

const KohlerChevronLeft = () => {
  return (
    <svg
      width="16"
      height="24"
      viewBox="0 0 16 24"
      fill="none"
      className={Styles.carouselChevron}
    >
      <polyline
        points="14,2 2,12 14,22"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="butt"
        strokeLinejoin="miter"
        fill="none"
      />
    </svg>
  )
}

const KohlerChevronRight = () => {
  return (
    <svg
      width="16"
      height="24"
      viewBox="0 0 16 24"
      fill="none"
      className={Styles.carouselChevron}
    >
      <polyline
        points="2,2 14,12 2,22"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="butt"
        strokeLinejoin="miter"
        fill="none"
      />
    </svg>
  )
}

const ThumbStrip = ({ images, activeIndex, continuousIndex, onThumbClick }) => {
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const realCount = images.length
  const thumbWidth =
    containerWidth > 0 ? containerWidth / (visibleFull + 2 * peekRatio) : 0

  const WINDOW_RADIUS = realCount * 3
  const windowStart = continuousIndex - WINDOW_RADIUS
  const windowEnd = continuousIndex + WINDOW_RADIUS
  const items = []
  for (let i = windowStart; i <= windowEnd; i++) {
    const realIdx = ((i % realCount) + realCount) % realCount
    items.push({ virtualIdx: i, realIdx })
  }

  const activePositionInStrip = (continuousIndex - windowStart) * thumbWidth
  const translateX =
    containerWidth > 0
      ? containerWidth / 2 - (activePositionInStrip + thumbWidth / 2)
      : 0

  return (
    <div ref={containerRef} className={Styles.carouselThumbStrip}>
      <div
        className={Styles.carouselThumbTrack}
        style={{
          width: items.length * thumbWidth || "100%",
          transform: `translateX(${translateX}px)`,
        }}
      >
        {items.map(({ virtualIdx, realIdx }) => {
          const isActive =
            realIdx === activeIndex && virtualIdx === continuousIndex
          return (
            <div
              key={virtualIdx}
              className={Styles.carouselThumbItem}
              style={{ width: thumbWidth, cursor: "pointer" }}
              onClick={() => onThumbClick?.(realIdx, virtualIdx)}
            >
              <img
                src={(() => {
                  const u =
                    images[realIdx]?.fields?.imageUrl ??
                    images[realIdx]?.fields?.assets?.[0]?.fields?.file?.url
                  return u?.startsWith("//") ? `https:${u}` : u
                })()}
                alt={
                  images[realIdx]?.fields?.alt ??
                  images[realIdx]?.fields?.assets?.[0]?.fields?.title ??
                  `Thumbnail ${realIdx + 1}`
                }
                className={`${Styles.carouselThumbImg} ${isActive ? Styles.carouselThumbImgActive : Styles.carouselThumbImgInactive}`}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const CustomCarouselBanner = ({ customCarouselBannerData = {} }) => {
  const [mainRef, setMainRef] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [continuousIndex, setContinuousIndex] = useState(0)
  const [trackHovered, setTrackHovered] = useState(false)
  const [hoverLeft, setHoverLeft] = useState(false)
  const [hoverRight, setHoverRight] = useState(false)
  const thumbClickedRef = useRef(false)

  const { imageList = [], title = "", removePadding=false } = customCarouselBannerData

  const handleThumbClick = (realIdx, virtualIdx) => {
    thumbClickedRef.current = true
    setActiveIndex(realIdx)
    setContinuousIndex(virtualIdx)
    mainRef?.slickGoTo(realIdx)
  }

  const handleMainBeforeChange = (current, next) => {
    if (thumbClickedRef.current) {
      thumbClickedRef.current = false
      return
    }
    setActiveIndex(next)
    setContinuousIndex(prev => {
      const total = imageList.length
      let step
      if (next === current) {
        step = 0
      } else if (next === (current + 1) % total) {
        step = 1
      } else if (next === (current - 1 + total) % total) {
        step = -1
      } else {
        let delta = next - current
        if (delta > total / 2) delta -= total
        if (delta < -total / 2) delta += total
        step = delta
      }
      return prev + step
    })
  }

  const mainSettings = {
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: false,
    beforeChange: handleMainBeforeChange,
  }

  const goNext = () => mainRef?.slickNext()
  const goPrev = () => mainRef?.slickPrev()

  const arrowOpacity = trackHovered || hoverLeft || hoverRight ? 1 : 0
  return (
    <div>
      <div className={Styles.carouselWrapper} style={{ paddingBottom: removePadding ? "0" : "100px" }}>
        {title && <div className={Styles.heading}>{title}</div>}
        {/* Main carousel */}
        <div
          className={Styles.carouselMainTrack}
          onMouseEnter={() => setTrackHovered(true)}
          onMouseLeave={() => {
            setTrackHovered(false)
            setHoverLeft(false)
            setHoverRight(false)
          }}
        >
          <Slider ref={slider => setMainRef(slider)} {...mainSettings}>
            {imageList?.map((img, i) => (
              <div key={i}>
                <img
                  src={(() => {
                    const u =
                      img?.fields?.imageUrl ??
                      img?.fields?.assets?.[0]?.fields?.file?.url
                    return u?.startsWith("//") ? `https:${u}` : u
                  })()}
                  alt={
                    img?.fields?.alt ??
                    img?.fields?.assets?.[0]?.fields?.title ??
                    `Slide ${i + 1}`
                  }
                  className={Styles.carouselMainImage}
                />
              </div>
            ))}
          </Slider>

          <button
            className={`${Styles.carouselArrow} ${Styles.carouselArrowLeft}`}
            style={{
              opacity: arrowOpacity,
              background: hoverLeft ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.10)",
            }}
            onClick={goPrev}
            onMouseEnter={() => setHoverLeft(true)}
            onMouseLeave={() => setHoverLeft(false)}
            aria-label="Previous"
          >
            <KohlerChevronLeft />
          </button>

          <button
            className={`${Styles.carouselArrow} ${Styles.carouselArrowRight}`}
            style={{
              opacity: arrowOpacity,
              background: hoverRight ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.10)",
            }}
            onClick={goNext}
            onMouseEnter={() => setHoverRight(true)}
            onMouseLeave={() => setHoverRight(false)}
            aria-label="Next"
          >
            <KohlerChevronRight />
          </button>
        </div>

        <ThumbStrip
          images={imageList}
          activeIndex={activeIndex}
          continuousIndex={continuousIndex}
          onThumbClick={handleThumbClick}
        />
      </div>
    </div>
  )
}

export default CustomCarouselBanner

CustomCarouselBanner.propTypes = {
  customCarouselBannerData: PropTypes.shape({
    imageList: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
}

ThumbStrip.defaultProps = {
  images: [],
  activeIndex: 0,
  continuousIndex: 0,
}

ThumbStrip.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeIndex: PropTypes.number.isRequired,
  continuousIndex: PropTypes.number.isRequired,
}
